import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import Layout from './Layout';
import FadeIn from './FadeIn';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  quote: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Sophie Martin",
    role: "DRH",
    company: "TechVision",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=200&h=200&q=80",
    quote: "PimpMyJob a transformé notre processus de recrutement. Nous gagnons un temps précieux et attirons de meilleurs talents.",
    rating: 5
  },
  {
    id: 2,
    name: "Thomas Dubois",
    role: "Talent Acquisition",
    company: "InnovCorp",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=200&h=200&q=80",
    quote: "La qualité des offres générées est impressionnante. L'IA comprend parfaitement notre ton et notre culture d'entreprise.",
    rating: 5
  },
  {
    id: 3,
    name: "Marie Leroy",
    role: "Responsable RH",
    company: "DataFlow",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=200&h=200&q=80",
    quote: "Un outil indispensable pour tout recruteur moderne. La personnalisation des offres est exceptionnelle.",
    rating: 5
  }
];

const TestimonialsSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextTestimonial = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
    
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevTestimonial = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
    
    setTimeout(() => setIsTransitioning(false), 500);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
      <Layout>
        <FadeIn>
          <div className="text-center mb-16">
            <h2 className="text-h2-mobile md:text-h2 mb-4">
              Ce que disent nos clients
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Découvrez comment PimpMyJob aide les entreprises à optimiser leur processus de recrutement.
            </p>
          </div>
        </FadeIn>

        <div className="relative max-w-4xl mx-auto">
          <button
            onClick={prevTestimonial}
            disabled={isTransitioning}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-110 disabled:opacity-50"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <button
            onClick={nextTestimonial}
            disabled={isTransitioning}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-110 disabled:opacity-50"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>

          <FadeIn>
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div 
                className={`flex flex-col items-center text-center transition-opacity duration-500 ${
                  isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}
              >
                <img
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].name}
                  className="w-20 h-20 rounded-full object-cover mb-6"
                  loading="lazy"
                />
                
                <div className="flex items-center mb-4">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>

                <blockquote className="text-xl md:text-2xl text-gray-800 mb-6 italic">
                  "{testimonials[currentIndex].quote}"
                </blockquote>

                <div>
                  <cite className="not-italic">
                    <span className="font-semibold text-gray-900 block">
                      {testimonials[currentIndex].name}
                    </span>
                    <span className="text-gray-600">
                      {testimonials[currentIndex].role} chez {testimonials[currentIndex].company}
                    </span>
                  </cite>
                </div>
              </div>
            </div>
          </FadeIn>

          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => !isTransitioning && setCurrentIndex(index)}
                disabled={isTransitioning}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-indigo-600 w-4' : 'bg-gray-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </Layout>
    </section>
  );
};

export default TestimonialsSection;